import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { FieldPath, pathToId } from "./types";
import { seedRequiredDefaults } from "./seedRequiredDefaults";
import { seedGeneratedFields } from "./seedGeneratedFields";

interface ChangeLogEntry {
  rowId: string;
  from: unknown;
  to: unknown;
  label: string;
}

export interface DraftState {
  initial: any;
  draft: any;
  dirtyIds: Set<string>;
  changeLog: ChangeLogEntry[];
}

type Action =
  | { type: "init"; data: any; schema: any; populateDefaults: boolean }
  | { type: "reset"; data: any; schema: any; populateDefaults: boolean }
  | { type: "setValue"; path: FieldPath; value: unknown; label: string; rowId: string }
  | { type: "addArrayItem"; path: FieldPath; item: unknown }
  | { type: "removeArrayItem"; path: FieldPath; index: number }
  | { type: "addOptionalField"; parentPath: FieldPath; key: string; defaultValue: unknown }
  | { type: "removeOptionalField"; parentPath: FieldPath; key: string }
  | { type: "toggleOptionalSection"; path: FieldPath; enabled: boolean; defaultBranchData: unknown }
  | { type: "discard" };

function clone<T>(v: T): T {
  return typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v));
}

function getIn(obj: any, path: FieldPath): any {
  let cur = obj;
  for (const seg of path) {
    if (cur == null) return undefined;
    cur = (cur as any)[seg];
  }
  return cur;
}

function setIn(obj: any, path: FieldPath, value: unknown): any {
  if (path.length === 0) return value;
  const next = Array.isArray(obj) ? [...obj] : { ...(obj ?? {}) };
  const [head, ...rest] = path;
  if (rest.length === 0) {
    (next as any)[head] = value;
  } else {
    (next as any)[head] = setIn((next as any)[head], rest, value);
  }
  return next;
}

function deleteIn(obj: any, path: FieldPath): any {
  if (path.length === 0) return undefined;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const idx = typeof head === "number" ? head : Number(head);
    if (rest.length === 0) {
      const next = [...obj];
      next.splice(idx, 1);
      return next;
    }
    const next = [...obj];
    next[idx] = deleteIn(next[idx], rest);
    return next;
  }
  const next = { ...(obj ?? {}) } as Record<string, unknown>;
  if (rest.length === 0) {
    delete next[String(head)];
    return next;
  }
  next[String(head)] = deleteIn(next[String(head)], rest);
  return next;
}

function reducer(state: DraftState, action: Action): DraftState {
  switch (action.type) {
    case "init":
    case "reset": {
      const base = action.populateDefaults
        ? seedRequiredDefaults(action.schema, action.data ?? {})
        : action.data ?? {};
      const seeded = seedGeneratedFields(action.schema, base);
      return {
        initial: clone(seeded),
        draft: clone(seeded),
        dirtyIds: new Set(),
        changeLog: [],
      };
    }
    case "setValue": {
      const prev = getIn(state.draft, action.path);
      if (prev === action.value) return state;
      // Treat undefined as a hard removal so Ajv sees the key as missing
      // (otherwise `obj.foo = undefined` still passes `required` validation).
      const draft =
        action.value === undefined
          ? deleteIn(state.draft, action.path)
          : setIn(state.draft, action.path, action.value);
      const initialValue = getIn(state.initial, action.path);
      const dirtyIds = new Set(state.dirtyIds);
      const filteredLog = state.changeLog.filter((e) => e.rowId !== action.rowId);
      // If the value matches the initial, treat as "no net change" and drop from dirty + log.
      // Also treat undefined/null/empty-string as equivalent (all mean "absent").
      const isEmptyish = (x: unknown) => x === undefined || x === null || x === "";
      const sameAsInitial =
        initialValue === action.value ||
        (isEmptyish(initialValue) && isEmptyish(action.value));
      if (sameAsInitial) {
        dirtyIds.delete(action.rowId);
        return { ...state, draft, dirtyIds, changeLog: filteredLog };
      }
      dirtyIds.add(action.rowId);
      return {
        ...state,
        draft,
        dirtyIds,
        changeLog: [
          ...filteredLog,
          { rowId: action.rowId, from: initialValue, to: action.value, label: action.label },
        ],
      };
    }
    case "addArrayItem": {
      const arr = (getIn(state.draft, action.path) as unknown[]) ?? [];
      const draft = setIn(state.draft, action.path, [...arr, action.item]);
      const parentRowId = pathToId(action.path);
      const itemRowId = pathToId([...action.path, arr.length]);
      const dirtyIds = new Set(state.dirtyIds);
      dirtyIds.add(itemRowId);
      const changeLog = [
        ...state.changeLog.filter((e) => e.rowId !== parentRowId),
        { rowId: parentRowId, from: arr.length, to: arr.length + 1, label: `${parentRowId}: + 1 item` },
      ];
      return { ...state, draft, dirtyIds, changeLog };
    }
    case "removeArrayItem": {
      const arr = (getIn(state.draft, action.path) as unknown[]) ?? [];
      const draft = deleteIn(state.draft, [...action.path, action.index]);
      const parentRowId = pathToId(action.path);
      const dirtyIds = new Set(state.dirtyIds);
      dirtyIds.add(parentRowId);
      const changeLog = [
        ...state.changeLog.filter((e) => e.rowId !== parentRowId),
        { rowId: parentRowId, from: arr.length, to: arr.length - 1, label: `${parentRowId}: − 1 item` },
      ];
      return { ...state, draft, dirtyIds, changeLog };
    }
    case "addOptionalField": {
      const childPath = [...action.parentPath, action.key];
      const draft = setIn(state.draft, childPath, action.defaultValue ?? null);
      const rowId = pathToId(childPath);
      const dirtyIds = new Set(state.dirtyIds);
      dirtyIds.add(rowId);
      const changeLog = [
        ...state.changeLog.filter((e) => e.rowId !== rowId),
        { rowId, from: undefined, to: action.defaultValue, label: `${rowId}: added` },
      ];
      return { ...state, draft, dirtyIds, changeLog };
    }
    case "removeOptionalField": {
      const childPath = [...action.parentPath, action.key];
      const prev = getIn(state.draft, childPath);
      const draft = deleteIn(state.draft, childPath);
      const rowId = pathToId(childPath);
      const dirtyIds = new Set(state.dirtyIds);
      const filteredLog = state.changeLog.filter((e) => e.rowId !== rowId);
      const initialValue = getIn(state.initial, childPath);
      const initialAbsent =
        initialValue === undefined || initialValue === null || initialValue === "";
      if (initialAbsent) {
        dirtyIds.delete(rowId);
        return { ...state, draft, dirtyIds, changeLog: filteredLog };
      }
      dirtyIds.add(rowId);
      return {
        ...state,
        draft,
        dirtyIds,
        changeLog: [
          ...filteredLog,
          { rowId, from: prev, to: undefined, label: `${rowId}: removed` },
        ],
      };
    }
    case "toggleOptionalSection": {
      const draft = action.enabled
        ? setIn(state.draft, action.path, action.defaultBranchData)
        : deleteIn(state.draft, action.path);
      const rowId = pathToId(action.path);
      const dirtyIds = new Set(state.dirtyIds);
      dirtyIds.add(rowId);
      const changeLog = [
        ...state.changeLog.filter((e) => e.rowId !== rowId),
        { rowId, from: action.enabled ? undefined : action.defaultBranchData, to: action.enabled ? action.defaultBranchData : undefined, label: `${rowId}: ${action.enabled ? "enabled" : "disabled"}` },
      ];
      return { ...state, draft, dirtyIds, changeLog };
    }
    case "discard": {
      return {
        ...state,
        draft: clone(state.initial),
        dirtyIds: new Set(),
        changeLog: [],
      };
    }
    default:
      return state;
  }
}

export interface UseDraftStateOpts {
  initialData: any;
  schema: any;
  populateDefaults: boolean;
  onDraftChange?: (draft: any) => void;
}

export function useDraftState(opts: UseDraftStateOpts) {
  const { initialData, schema, populateDefaults, onDraftChange } = opts;
  const [state, dispatch] = useReducer(reducer, undefined as any, () => ({
    initial: {},
    draft: {},
    dirtyIds: new Set<string>(),
    changeLog: [],
  }));

  const initSig = useRef<string>("");
  useEffect(() => {
    if (!schema) return;
    // initialData is derived asynchronously; re-seed when it arrives, but never
    // stomp edits the user has already started mid-seed.
    const sig = JSON.stringify({ populateDefaults, initial: initialData ?? null });
    if (sig === initSig.current) return;
    if (state.dirtyIds.size > 0) return;
    initSig.current = sig;
    dispatch({ type: "init", data: initialData, schema, populateDefaults });
  }, [schema, populateDefaults, initialData, state.dirtyIds.size]);

  useEffect(() => {
    if (onDraftChange) onDraftChange(state.draft);
  }, [state.draft, onDraftChange]);

  const commit = useCallback(
    (path: FieldPath, value: unknown, label: string) => {
      dispatch({ type: "setValue", path, value, label, rowId: pathToId(path) });
    },
    []
  );

  return useMemo(() => ({ state, dispatch, commit }), [state, dispatch, commit]);
}
